import { useEffect, useRef } from "react";
import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "react-query";
import type { Center } from "../../../types/center.types";
import type { Patient } from "../../../types/patient.types";
import { useCenterAudio, useCenters } from "../../../hooks/centers.hooks";
import { useNewOrderStore } from "../../../stores/order.store";
import { createPatient } from "../../../mutations/patient.mutations";
import PageTitle from "../../../components/PageTitle";
import CenterSelect from "../../../components/inputs/CenterSelect";
import PatientSelect from "../../../components/inputs/PatientSelect";
import SimpleModal from "../../../components/modals/SimpleModal";
import AddUserIcon from "../../../components/icons/AddUserIcon";
import PatientForm, {
	type PatientFormHandler,
} from "../../../components/forms/PatientForm";
import { SelectUserCircle } from "./SelectUserCircle";
import { OrdersNewPageBaseLayout } from "./OrdersNewPageBaseLayout";
import { OrdersNewPageBaseLayoutButton } from "./OrdersNewPageBaseLayoutButton";

/**
 * Layout for center, audio responsible, and patient selection.
 */
export const ChooseAudioResponsibleLayout = () => {
	const { center, setCurrentCenter } = useCenters();
	const { getCenter: getOrderCenter, setCenter: setOrderCenter } =
		useNewOrderStore();
	const { audioResponsible, setAudioResponsible } = useNewOrderStore();
	const { data: audios } = useCenterAudio(getOrderCenter()?.id);
	const { setPatient, patient } = useNewOrderStore();
	const newPatientModal = useDisclosure();
	const newPatientFormRef = useRef<PatientFormHandler>(null);
	const queryClient = useQueryClient();
	const { mutate: mutateCreatePatient } = useMutation(createPatient, {
		onSuccess: (data: Patient) => {
			setPatient(data);
			newPatientModal.onClose();
			queryClient.invalidateQueries("center-patients");
		},
	});

	useEffect(() => {
		/**
		 * If first visit on the page, orderCenter should be null.
		 * In that case load the currently selected center as the default center.
		 */
		if (center && !getOrderCenter()) {
			setOrderCenter(center);
		}
	}, [center, getOrderCenter, setOrderCenter]);
	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={8} mb={8} alignItems="center">
				<PageTitle>Qui est le patient ?</PageTitle>
				<CenterSelect
					centerSelected={getOrderCenter() ?? undefined}
					setCenterSelected={(center?: Center) => {
						setCurrentCenter(center ?? null);
						setOrderCenter(center);
					}}
				/>
				<Flex w="full" justifyContent="start">
					<Text textAlign="left" fontSize="14px" fontWeight={500}>
						Qui est l'audioprothésiste responsable ?
					</Text>
				</Flex>
				<SelectUserCircle
					centerId={getOrderCenter()?.id}
					users={audios?.data ?? []}
					w="min(44rem, 90vw)"
					userSelected={audioResponsible}
					setUserSelected={(user) => {
						setAudioResponsible(user);
					}}
				/>
				<Flex direction="column" w="full">
					<PatientSelect
						center={getOrderCenter()}
						patientSelected={patient}
						setPatientSelected={(patient?: Patient) => {
							setPatient(patient);
						}}
						onNewPatient={() => newPatientModal.onOpen()}
					/>
					<SimpleModal
						icon={<AddUserIcon w="32px" h="32px" />}
						title="Ajouter un patient"
						isOpen={newPatientModal.isOpen}
						onClose={newPatientModal.onClose}
						onClick={async () => {
							const center = getOrderCenter();
							if (newPatientFormRef.current && center) {
								const valid = await newPatientFormRef.current.valid();
								if (valid) {
									const patient = newPatientFormRef.current.values();
									mutateCreatePatient({
										patient: {
											...patient,
											centerId: center.id,
										},
									});
								}
							}
						}}
					>
						<PatientForm ref={newPatientFormRef} />
					</SimpleModal>
				</Flex>
				<OrdersNewPageBaseLayoutButton />
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};
